import WellItemDragData from '../store/schema/WellItemDragData';
import { observer } from 'mobx-react';
import DragAndDroppable from 'owa-dnd/lib/components/DragAndDroppable';
import DragViewState from 'owa-dnd/lib/store/schema/DragViewState';
import DropEffect from 'owa-dnd/lib/store/schema/DropEffect';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { getStyles, DraggableItemStyle, DraggableItemStyleProps } from './DraggableItem.styles';
import { classNamesFunction } from '@uifabric/utilities';
import WellDropViewState from '../store/schema/WellDropViewState';

import * as React from 'react';
const getClassNames = classNamesFunction<DraggableItemStyleProps, DraggableItemStyle>();
let classNamesUtil = require('classnames');

const DRAG_X_OFFSET = -10;
const DRAG_Y_OFFSET = 5;

export interface DraggableItemProps<T> {
    item: T;
    itemIndex: number;
    displayText: string;
    draggableItemType: string;
    dragViewState: DragViewState;
    dropViewState: WellDropViewState;
    removeDroppedItemAction: (item: T, shouldRemoveFromRight?) => void;
    canDrag: (item: T) => boolean;
}

@observer
export default class DraggableItem<T> extends React.Component<DraggableItemProps<T>, any> {
    private dragData: WellItemDragData;
    private dropViewState: WellDropViewState;
    private isDropped: boolean;

    constructor(props: DraggableItemProps<T>) {
        super(props);

        this.dragData = {
            itemType: this.props.draggableItemType,
            item: this.props.item,
            displayText: this.props.displayText,
        };

        this.dropViewState = this.props.dropViewState
            ? this.props.dropViewState
            : createDropViewState();

        this.state = { isItemDraggedOver: false, didDragStart: false };
    }

    render() {
        const { didDragStart, isItemDraggedOver } = this.state;
        const classNames = getClassNames(getStyles, {
            isItemDraggedOver,
            didDragStart: this.dropViewState ? this.dropViewState.didDragStart : false,
        });
        let draggedClass = didDragStart ? 'draggedItem' : '';
        return (
            <DragAndDroppable
                classNames={classNamesUtil(classNames.draggableContainer, draggedClass)}
                dragViewState={this.props.dragViewState}
                dropViewState={this.props.dropViewState}
                canDrag={this.canDrag}
                onDrop={this.onDrop}
                onDragLeave={this.onDragLeave}
                onDragOver={this.onDragOver}
                onDragStart={this.onDragStart}
                getDragData={this.getDragData}
                getDragPreview={this.getDragPreview}
                xOffset={DRAG_X_OFFSET}
                yOffset={DRAG_Y_OFFSET}
                onDragEnd={this.onDragEnd}>
                {this.props.children}
            </DragAndDroppable>
        );
    }

    private onDragStart = (dragData: WellItemDragData, pageX: number, pageY: number) => {
        this.dropViewState.didDragStart = true;
    };

    private onDragEnd = (dragData: WellItemDragData, dropEffect?: DropEffect) => {
        this.dropViewState.didDragStart = false;
        // If succesfully dropped, the dropEffect should be set to 'move'
        if (dropEffect && dropEffect == 'move') {
            this.props.removeDroppedItemAction(
                this.props.item,
                this.dropViewState.shouldRemoveFromRight
            );
        }
    };

    private onDragOver = (dragInfo: WellItemDragData) => {
        this.isDropped = false;
        this.setState({ isItemDraggedOver: true });
    };

    private onDragLeave = (dragInfo: WellItemDragData) => {
        if (!this.isDropped) {
            this.dropViewState.dropItemIndex = -1;
        }
        this.setState({ isItemDraggedOver: false });
    };

    private onDrop = (dragInfo: WellItemDragData, pageX: number, pageY: number) => {
        this.isDropped = true;
        this.dropViewState.dropItemIndex = this.props.itemIndex;
    };

    private canDrag = (): boolean => {
        const { item } = this.props;
        return this.props.canDrag(item);
    };

    private getDragData = (): WellItemDragData => {
        return this.dragData;
    };

    private getDragPreview(dragData: WellItemDragData): HTMLElement {
        const elem = document.createElement('div');
        elem.innerText = dragData.displayText;
        return elem;
    }
}
